import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';

import VMPGenericChip from '@/components/VMPGenericChip';
import { VMP_SOLID_COLORS } from '@/constants/chartcolors.constants';
import { assetRiskScoreDialogAtom } from '@/pages/Assets/Assets.store';
import type { CriticalityTypes } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import getPonduranceScoreSeverity from '@/utils/getPonduranceScoreSeverity';

import useStyles from './AssetRiskScoreDialog.styles';

const AssetRiskScoreDialog = () => {
  const { classes } = useStyles();
  const [assetRiskScoreDialog, setAssetRiskScoreDialog] = useAtom(
    assetRiskScoreDialogAtom
  );

  const defenseRisk = assetRiskScoreDialog?.risk_score_details?.rd_defense
    ? getPonduranceScoreSeverity(
        assetRiskScoreDialog?.risk_score_details.rd_defense
      )
    : 'N/A';
  const threatsRisk = assetRiskScoreDialog?.risk_score_details?.rt_threats
    ? getPonduranceScoreSeverity(
        assetRiskScoreDialog?.risk_score_details.rt_threats
      )
    : 'N/A';
  const exposureRisk = assetRiskScoreDialog?.risk_score_details?.re_exposure
    ? getPonduranceScoreSeverity(
        assetRiskScoreDialog?.risk_score_details.re_exposure
      )
    : 'N/A';
  const importanceRisk = assetRiskScoreDialog?.risk_score_details?.ri_importance
    ? getPonduranceScoreSeverity(
        assetRiskScoreDialog?.risk_score_details.ri_importance
      )
    : 'N/A';
  const vulnerabilityRisk = assetRiskScoreDialog?.risk_score_details
    ?.rv_vulnerability
    ? getPonduranceScoreSeverity(
        assetRiskScoreDialog?.risk_score_details.rv_vulnerability
      )
    : 'N/A';

  const overallRisk = getPonduranceScoreSeverity(
    assetRiskScoreDialog?.risk_score || 0
  );

  return (
    <Dialog
      open={assetRiskScoreDialog !== null}
      onClose={() => setAssetRiskScoreDialog(null)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Risk Score Explanation</DialogTitle>
      <IconButton
        className={classes.assetRiskScoreIconButtonCancel}
        onClick={() => {
          setAssetRiskScoreDialog(null);
        }}
      >
        <FontAwesomeIcon size="xs" icon={faXmark} />
      </IconButton>
      <DialogContent>
        <List className={classes.assetRiskScoreList}>
          <ListItem
            sx={{
              backgroundColor: `${VMP_SOLID_COLORS[overallRisk.toUpperCase() as CriticalityTypes]}40 !important`
            }}
            className={classes.assetRiskScoreListItem}
          >
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Overall Risk</Typography>
              <VMPGenericChip
                type="filled"
                value={overallRisk}
                text={overallRisk}
              />
            </Stack>
            <Typography component="span">
              This asset has a calculated overall risk score of{' '}
              <Chip
                size="small"
                label={Math.round(assetRiskScoreDialog?.risk_score || 0)}
                sx={{ fontWeight: 800, mx: 0.5 }}
              />{' '}
              based on the categories listed below.
            </Typography>
          </ListItem>
          <ListItem className={classes.assetRiskScoreListItem}>
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Defense Risk</Typography>
              {defenseRisk && (
                <VMPGenericChip
                  type="filled"
                  value={defenseRisk}
                  text={defenseRisk}
                />
              )}
            </Stack>
            <Typography>
              This measures how well the asset is protected by security tools.
              Lacking strong defenses, such as EDR or Pondurance Sidecar agent,
              firewall/network monitoring and vulnerability scanning will result
              in a higher risk score.
            </Typography>
          </ListItem>
          <ListItem className={classes.assetRiskScoreListItem}>
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Threats Risk</Typography>
              {threatsRisk && (
                <VMPGenericChip
                  type="filled"
                  value={threatsRisk}
                  text={threatsRisk}
                />
              )}
            </Stack>
            <Typography>
              This indicates whether the asset is currently associated with
              suspicious or malicious activity. Assets with high threat risk are
              actively experiencing alerts or attacks, while those with low risk
              typically have few or no alerts detected
            </Typography>
          </ListItem>
          <ListItem className={classes.assetRiskScoreListItem}>
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Exposure Risk</Typography>
              {exposureRisk && (
                <VMPGenericChip
                  type="filled"
                  value={exposureRisk}
                  text={exposureRisk}
                />
              )}
            </Stack>
            <Typography>
              This indicates how accessible the asset is from outside of your
              organization. Publicly exposed assets, especially those open to
              remote access tools, carry the highest risk. Note that Exposure
              and Vulnerability are key components of an asset&apos;s
              &quot;Attack Surface&quot;.
            </Typography>
          </ListItem>
          <ListItem className={classes.assetRiskScoreListItem}>
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Criticality Risk</Typography>
              {importanceRisk && (
                <VMPGenericChip
                  type="filled"
                  value={importanceRisk}
                  text={importanceRisk}
                />
              )}
            </Stack>
            <Typography>
              This reflects how critical the asset is to your business. This
              score is calculated based on the asset type. Note that you can
              manually override the criticality of an asset to affect this
              score.
            </Typography>
          </ListItem>
          <ListItem className={classes.assetRiskScoreListItem}>
            <Stack
              direction="row"
              spacing={2}
              className={classes.assetRiskScoreStack}
            >
              <Typography variant="h6">Vulnerability Risk</Typography>
              {vulnerabilityRisk && (
                <VMPGenericChip
                  type="filled"
                  value={vulnerabilityRisk}
                  text={vulnerabilityRisk}
                />
              )}
            </Stack>
            <Typography>
              This shows how susceptible the asset is to known vulnerabilities.
              Assets with critical or high vulnerability risk have serious flaws
              that attackers could exploit. The score is calculated based on the
              vulnerabilities found via the VMPE service.
            </Typography>
          </ListItem>
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default AssetRiskScoreDialog;
